import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  Search, 
  Filter, 
  MapPin, 
  Users, 
  Clock, 
  DollarSign,
  Mail,
  Phone,
  Globe,
  Instagram,
  Plus,
  ChevronDown,
  ChevronUp,
  Download,
  Upload,
  FileJson,
  Edit,
  Trash2,
  AlertCircle
} from 'lucide-react';
import { venuesData as staticVenuesData } from '../data/venues';
import { exportVenues, exportToJSON } from '../utils/export';
import { api } from '../utils/api';
import VenueModal from '../components/VenueModal';
import Modal from '../components/Modal';

const VenuesPage = () => {
  const [venues, setVenues] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [expandedVenue, setExpandedVenue] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [deletingVenue, setDeletingVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initializing, setInitializing] = useState(false);
  const [importingVenues, setImportingVenues] = useState(false);
  const fileInputRef = useRef(null);

  const venueTypes = ['all', 'Nightclub', 'Rooftop', 'Beach Club', 'Industrial', 'Cultural Hub', 'After-hours'];

  // Load venues from API
  useEffect(() => {
    loadVenues();
  }, []);

  const loadVenues = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.venues.getAll();
      
      // If no venues in KV, initialize with static data
      if (response.venues.length === 0 && staticVenuesData.length > 0) {
        await initializeVenues();
      } else {
        setVenues(response.venues);
      }
    } catch (error) {
      console.error('Error loading venues:', error);
      setError('Failed to load venues');
      // Fallback to static data
      setVenues(staticVenuesData);
    } finally {
      setLoading(false);
    }
  };

  // Initialize KV storage with static venues data
  const initializeVenues = async () => {
    try {
      setInitializing(true);
      console.log('Initializing venues in KV storage...');
      
      for (const venue of staticVenuesData) {
        await api.venues.create({
          ...venue,
          type: Array.isArray(venue.type) ? venue.type.join(', ') : venue.type
        });
      }
      
      // Reload venues after initialization
      const response = await api.venues.getAll();
      setVenues(response.venues);
      console.log('Venues initialized successfully');
    } catch (error) {
      console.error('Error initializing venues:', error);
      setError('Failed to initialize venues');
      setVenues(staticVenuesData);
    } finally {
      setInitializing(false);
    }
  };

  const filteredVenues = useMemo(() => {
    return venues.filter(venue => {
      const matchesSearch = venue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          venue.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          venue.notes?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || venue.type.includes(filterType);
      return matchesSearch && matchesType;
    });
  }, [venues, searchTerm, filterType]);

  const toggleVenueExpand = (venueId) => {
    setExpandedVenue(expandedVenue === venueId ? null : venueId);
  };

  const handleAddVenue = async (venueData) => {
    try {
      const response = await api.venues.create(venueData);
      setVenues([...venues, response.venue]);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding venue:', error);
      throw error;
    }
  };

  const handleEditVenue = async (venueData) => {
    try {
      const response = await api.venues.update(editingVenue.id, venueData);
      setVenues(venues.map(v => v.id === editingVenue.id ? response.venue : v));
      setEditingVenue(null);
    } catch (error) {
      console.error('Error updating venue:', error);
      throw error;
    }
  };

  const handleDeleteVenue = async () => {
    try {
      await api.venues.delete(deletingVenue.id);
      setVenues(venues.filter(v => v.id !== deletingVenue.id));
      setDeletingVenue(null);
      if (expandedVenue === deletingVenue.id) {
        setExpandedVenue(null);
      }
    } catch (error) {
      console.error('Error deleting venue:', error);
      setError('Failed to delete venue');
    }
  };

  const handleImportVenues = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportingVenues(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const isJSON = file.name.endsWith('.json');
          
          let venuesToImport = [];
          
          if (isJSON) {
            // Handle JSON import
            venuesToImport = JSON.parse(text);
            if (!Array.isArray(venuesToImport)) {
              venuesToImport = [venuesToImport];
            }
          } else {
            // Handle CSV import
            const rows = text.split('\n');
            const headers = rows[0].split(',').map(h => h.trim().toLowerCase());
            
            for (let i = 1; i < rows.length; i++) {
              if (!rows[i].trim()) continue;
              
              const values = rows[i].split(',').map(v => v.trim());
              const venue = {};
              
              headers.forEach((header, index) => {
                if (header === 'types' || header === 'tags') {
                  venue[header] = values[index] ? values[index].split(';').map(t => t.trim()) : [];
                } else if (header === 'capacity' || header === 'rating') {
                  venue[header] = parseInt(values[index]) || 0;
                } else {
                  venue[header] = values[index] || '';
                }
              });
              
              if (venue.name) {
                venuesToImport.push(venue);
              }
            }
          }
          
          // Import venues one by one
          let importCount = 0;
          for (const venue of venuesToImport) {
            try {
              await api.venues.create(venue);
              importCount++;
            } catch (error) {
              console.error(`Failed to import venue ${venue.name}:`, error);
            }
          }
          
          await loadVenues();
          alert(`Successfully imported ${importCount} venues`);
        } catch (error) {
          console.error('Error parsing file:', error);
          setError(`Failed to parse ${file.name.endsWith('.json') ? 'JSON' : 'CSV'} file`);
        }
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('Error importing venues:', error);
      setError('Failed to import venues');
    } finally {
      setImportingVenues(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A574] mx-auto mb-4"></div>
          <p className="text-[#888888]">{initializing ? 'Initializing venues...' : 'Loading venues...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light mb-2">Venues</h1>
          <p className="text-[#888888]">Manage and explore {venues.length} venues across Lisboa</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary flex items-center gap-2"
            disabled={importingVenues}
          >
            <Upload size={20} />
            {importingVenues ? 'Importing...' : 'Import'}
          </button>
          <button 
            onClick={() => exportVenues(filteredVenues)}
            className="btn-secondary flex items-center gap-2"
          >
            <Download size={20} />
            Export CSV
          </button>
          <button 
            onClick={() => exportToJSON(filteredVenues, 'venues.json')}
            className="btn-secondary flex items-center gap-2"
          >
            <FileJson size={20} />
            Export JSON
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Add Venue
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 text-[#888888]" size={20} />
          <input
            type="text"
            placeholder="Search venues..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-dark w-full pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="text-[#888888]" size={20} />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input-dark"
          >
            {venueTypes.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Types' : type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">{filteredVenues.length}</p>
          <p className="text-sm text-[#888888]">Showing</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">
            {filteredVenues.filter(v => v.type.includes('Nightclub')).length}
          </p>
          <p className="text-sm text-[#888888]">Nightclubs</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">
            {filteredVenues.filter(v => v.type.includes('Rooftop')).length}
          </p>
          <p className="text-sm text-[#888888]">Rooftops</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">
            {filteredVenues.filter(v => v.type.includes('Beach')).length}
          </p>
          <p className="text-sm text-[#888888]">Beach Clubs</p>
        </div>
      </div>

      {/* Venues List */}
      <div className="space-y-4">
        {filteredVenues.map((venue) => (
          <div key={venue.id} className="card-dark">
            {/* Venue Header */}
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => toggleVenueExpand(venue.id)}
            >
              <div className="flex-1">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#0A0A0A] rounded-lg">
                    <MapPin className="text-[#D4A574]" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-medium mb-1">{venue.name}</h3>
                    <p className="text-sm text-[#888888] mb-2">{venue.type}</p>
                    <p className="text-sm text-[#CCCCCC]">{venue.address}</p>
                  </div>
                </div>
              </div>
              <button className="p-2 hover:bg-[#0A0A0A] rounded-lg transition-colors">
                {expandedVenue === venue.id ? <ChevronUp /> : <ChevronDown />}
              </button>
            </div>

            {/* Expanded Details */}
            {expandedVenue === venue.id && (
              <div className="mt-6 pt-6 border-t border-[#333333] animate-slide-up">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Contact Info */}
                  <div>
                    <h4 className="text-sm font-medium text-[#888888] mb-3">Contact</h4>
                    <div className="space-y-2">
                      {venue.contact?.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail size={16} className="text-[#888888]" />
                          <a href={`mailto:${venue.contact.email}`} className="hover:text-[#D4A574]">
                            {venue.contact.email}
                          </a>
                        </div>
                      )}
                      {venue.contact?.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone size={16} className="text-[#888888]" />
                          <a href={`tel:${venue.contact.phone}`} className="hover:text-[#D4A574]">
                            {venue.contact.phone}
                          </a>
                        </div>
                      )}
                      {venue.website && (
                        <div className="flex items-center gap-2 text-sm">
                          <Globe size={16} className="text-[#888888]" />
                          <a href={`https://${venue.website}`} target="_blank" rel="noopener noreferrer" 
                             className="hover:text-[#D4A574]">
                            {venue.website}
                          </a>
                        </div>
                      )}
                      {venue.social && (
                        <div className="flex items-center gap-2 text-sm">
                          <Instagram size={16} className="text-[#888888]" />
                          <span>{venue.social}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Venue Details */}
                  <div>
                    <h4 className="text-sm font-medium text-[#888888] mb-3">Details</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Users size={16} className="text-[#888888]" />
                        <span>Capacity: {venue.capacity}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock size={16} className="text-[#888888]" />
                        <span>{venue.hours}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign size={16} className="text-[#888888]" />
                        <span>{venue.pricing}</span>
                      </div>
                    </div>
                  </div>

                  {/* Technical & Notes */}
                  <div>
                    <h4 className="text-sm font-medium text-[#888888] mb-3">Technical</h4>
                    <div className="space-y-2">
                      <p className="text-sm"><span className="text-[#888888]">Sound:</span> {venue.soundSystem}</p>
                      <p className="text-sm"><span className="text-[#888888]">Best for:</span> {venue.bestFor}</p>
                      {venue.notes && (
                        <p className="text-sm"><span className="text-[#888888]">Notes:</span> {venue.notes}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 pt-6 border-t border-[#333333] flex gap-3">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingVenue(venue);
                    }}
                    className="btn-secondary text-sm flex items-center gap-2"
                  >
                    <Edit size={16} />
                    Edit Details
                  </button>
                  <button className="btn-secondary text-sm">Add to Event</button>
                  <button className="btn-secondary text-sm">View Analytics</button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletingVenue(venue);
                    }}
                    className="btn-secondary text-sm text-red-500 hover:bg-red-500/10 flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit Venue Modal */}
      <VenueModal
        isOpen={showAddModal || !!editingVenue}
        onClose={() => {
          setShowAddModal(false);
          setEditingVenue(null);
        }}
        venue={editingVenue}
        onSave={editingVenue ? handleEditVenue : handleAddVenue}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingVenue}
        onClose={() => setDeletingVenue(null)}
        title="Delete Venue"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p>Are you sure you want to delete <strong>{deletingVenue?.name}</strong>?</p>
          <p className="text-sm text-[#888888]">This action cannot be undone.</p>
          
          <div className="flex gap-3 justify-end pt-4">
            <button
              onClick={() => setDeletingVenue(null)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteVenue}
              className="btn-primary bg-red-500 hover:bg-red-600"
            >
              Delete Venue
            </button>
          </div>
        </div>
      </Modal>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.json"
        onChange={handleImportVenues}
        className="hidden"
      />
    </div>
  );
};

export default VenuesPage;